// frontend/src/widgets/TaxonomyAdminWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-08-22
// Updated:      2026-09-09 15:31 BST
//
// Description:
//   Compact taxonomy admin widget for the EXISTING JOBS PAGE.
//   - READS subtrees from /taxonomy (no 404 spam) so lists always populate
//   - RESTORES New/Edit for Subtypes and Subtype2
//   - Type CRUD stays; adds client-side guards to avoid common 500s
//
// Endpoints (relative to /api):
//   READ:   GET taxonomy/by-type/{short_code}  (preferred)
//           GET taxonomy/tree                  (fallback)
//   TYPES:  GET job-types?sort=name:asc&search=..
//           POST job-types
//           PUT job-types/{id}
//           PATCH job-types/{id}/activate | job-types/{id}/deactivate
//           DELETE job-types/{id}
//   SUBS:   POST/PUT/PATCH/DELETE job-subtypes, job-subtypes2 (calls made if you click)
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves the existing Type -> Subtype -> Subtype2 hierarchy and all CRUD handlers.
//   - Header/search/actions stack cleanly on mobile with touch-sized controls.
//   - The three taxonomy panes stack vertically on mobile and remain three columns on desktop.
//   - Each populated mobile pane shows one complete taxonomy row per internal vertical
//     scroll/snap; desktop list scrolling remains unchanged.
//   - Dynamic mobile sizing uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary pane/root sizing;
//       measure the first rendered row in each taxonomy pane;
//       size each pane viewport to one complete row;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - Type/Subtype/Subtype2 modal fields collapse to one column on mobile and action
//     buttons become touch-sized/full-width.
//   - Long names/descriptions/action rows wrap instead of overflowing narrow screens.
//   - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//   - Existing taxonomy reads, fallback reads, mutation endpoints, selection persistence,
//     refresh behaviour and onSelect payload are unchanged.
//   - Existing user/tenant values are used only for localStorage preference scoping;
//     no tenant_id is added to API requests.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import ModalWrapper from "../components/ModalWrapper";
import { getWidgetTitle } from "../utils/widgetUtils";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";

// ---------- User/Tenant scoped persistence ----------
const decodeJWT = (token) => {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
};
const getUserTenantKey = () => {
  const token =
    localStorage.getItem("anpr_token") || localStorage.getItem("token") || "";
  const p = decodeJWT(token) || {};
  const userId = p.sub || p.user_id || p.uid || "unknownUser";
  const tenantId = p.tenant_id || p.tid || "unknownTenant";
  return { userId, tenantId };
};
const selectionKey = (userId, tenantId) =>
  `anpr_taxonomy_selection_${tenantId}_${userId}`;

// ---------- Inline helpers ----------
const TextInput = (props) => (
  <input
    {...props}
    className={
      "min-h-[44px] text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full md:min-h-0 " +
      (props.className || "")
    }
  />
);

const NumberInput = (props) => (
  <input
    type="number"
    {...props}
    className={
      "min-h-[44px] text-sm text-black border border-gray-300 bg-white rounded px-2 py-1 w-full md:min-h-0 md:w-24 " +
      (props.className || "")
    }
  />
);

const Checkbox = ({ checked, onChange }) => (
  <input className="h-5 w-5 md:h-auto md:w-auto" type="checkbox" checked={!!checked} onChange={onChange} />
);

// ---------- Create/Edit Modals ----------
const TypeModal = ({ open, onClose, initial, onSave }) => {
  const [form, setForm] = useState(
    initial || { short_code: "", name: "", description: "", sort_order: 0, is_active: true }
  );
  useEffect(() => {
    setForm(
      initial || { short_code: "", name: "", description: "", sort_order: 0, is_active: true }
    );
  }, [initial]);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      short_code: (form.short_code || "").trim().toUpperCase(),
      name: (form.name || "").trim(),
      sort_order: Number(form.sort_order) || 0,
    });
  };

  if (!open) return null;
  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">
        {initial?.id ? "Edit Job Type" : "New Job Type"}
      </h3>
      <form onSubmit={submit} className="grid gap-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="text-sm text-gray-700">Short Code</label>
            <TextInput
              required
              value={form.short_code || ""}
              onChange={(e) =>
                setForm((s) => ({ ...s, short_code: e.target.value.toUpperCase() }))
              }
            />
          </div>
          <div>
            <label className="text-sm text-gray-700">Name</label>
            <TextInput
              required
              value={form.name || ""}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-700">Description</label>
          <textarea
            className="min-h-[96px] w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-black md:min-h-[72px] md:py-1"
            value={form.description || ""}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div>
            <label className="text-sm text-gray-700">Sort</label>
            <NumberInput
              value={Number(form.sort_order) || 0}
              onChange={(e) =>
                setForm((s) => ({ ...s, sort_order: Number(e.target.value) }))
              }
            />
          </div>
          <label className="flex min-h-[44px] items-center gap-2 sm:mt-5">
            <Checkbox
              checked={!!form.is_active}
              onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        </div>
        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            Cancel
          </button>
          <button
            className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            {initial?.id ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const SubtypeModal = ({ open, onClose, initial, onSave, parentType }) => {
  const [form, setForm] = useState(
    initial || { name: "", description: "", sort_order: 0, is_active: true }
  );
  useEffect(() => {
    setForm(initial || { name: "", description: "", sort_order: 0, is_active: true });
  }, [initial]);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      name: (form.name || "").trim(),
      sort_order: Number(form.sort_order) || 0,
    });
  };

  if (!open) return null;
  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">
        {initial?.id ? "Edit Subtype" : "New Subtype"}
        <span className="ml-2 text-sm text-gray-600">
          for {parentType?.name} ({parentType?.short_code})
        </span>
      </h3>
      <form onSubmit={submit} className="grid gap-3">
        <div>
          <label className="text-sm text-gray-700">Name</label>
          <TextInput
            required
            value={form.name || ""}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-sm text-gray-700">Description</label>
          <textarea
            className="min-h-[96px] w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-black md:min-h-[72px] md:py-1"
            value={form.description || ""}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div>
            <label className="text-sm text-gray-700">Sort</label>
            <NumberInput
              value={Number(form.sort_order) || 0}
              onChange={(e) =>
                setForm((s) => ({ ...s, sort_order: Number(e.target.value) }))
              }
            />
          </div>
          <label className="flex min-h-[44px] items-center gap-2 sm:mt-5">
            <Checkbox
              checked={!!form.is_active}
              onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        </div>
        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            Cancel
          </button>
          <button
            className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            {initial?.id ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const Subtype2Modal = ({ open, onClose, onSave, parentSubtype }) => {
  const [form, setForm] = useState({ name: "", description: "", sort_order: 0, is_active: true });
  useEffect(() => {
    if (!open) setForm({ name: "", description: "", sort_order: 0, is_active: true });
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      name: (form.name || "").trim(),
      sort_order: Number(form.sort_order) || 0,
    });
  };

  if (!open) return null;
  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">
        {`New Subtype 2`}{" "}
        <span className="ml-2 text-sm text-gray-600">under {parentSubtype?.name}</span>
      </h3>
      <form onSubmit={submit} className="grid gap-3">
        <div>
          <label className="text-sm text-gray-700">Name</label>
          <TextInput
            required
            value={form.name || ""}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-sm text-gray-700">Description</label>
          <textarea
            className="min-h-[96px] w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-black md:min-h-[72px] md:py-1"
            value={form.description || ""}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div>
            <label className="text-sm text-gray-700">Sort</label>
            <NumberInput
              value={Number(form.sort_order) || 0}
              onChange={(e) =>
                setForm((s) => ({ ...s, sort_order: Number(e.target.value) }))
              }
            />
          </div>
          <label className="flex min-h-[44px] items-center gap-2 sm:mt-5">
            <Checkbox
              checked={!!form.is_active}
              onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        </div>
        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            Cancel
          </button>
          <button
            className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
          >
            Create
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

// ---------- Main Widget ----------
const TaxonomyAdminWidget = ({ onSelect, initialShortCode, className }) => {
  const { userId, tenantId } = useMemo(getUserTenantKey, []);
  const persistKey = useMemo(() => selectionKey(userId, tenantId), [userId, tenantId]);
  const { refreshKey } = useDashboardRefresh();

  const widgetRootRef = useRef(null);
  const bodyRef = useRef(null);

  const typesViewportRef = useRef(null);
  const subtypesViewportRef = useRef(null);
  const subtypes2ViewportRef = useRef(null);

  const firstTypeRowRef = useRef(null);
  const firstSubtypeRowRef = useRef(null);
  const firstSubtype2RowRef = useRef(null);

  // Lists
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypes2, setSubtypes2] = useState([]);

  // Selections
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState(null);
  const [selectedSubtype2Id, setSelectedSubtype2Id] = useState(null);

  const selectedType = useMemo(
    () => types.find((t) => t.id === selectedTypeId) || null,
    [types, selectedTypeId]
  );
  const selectedSubtype = useMemo(
    () => subtypes.find((s) => s.id === selectedSubtypeId) || null,
    [subtypes, selectedSubtypeId]
  );
  const selectedSubtype2 = useMemo(
    () => subtypes2.find((s) => s.id === selectedSubtype2Id) || null,
    [subtypes2, selectedSubtype2Id]
  );

  // UI state
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState({ types: false, subs: false, subs2: false });
  const [err, setErr] = useState(null);

  // Cache taxonomy node for current type
  const taxNodeRef = useRef(null);

  // Persist/restore selection
  useEffect(() => {
    try {
      const raw = localStorage.getItem(persistKey);
      if (raw) {
        const saved = JSON.parse(raw);
        setSelectedTypeId(saved.typeId || null);
        setSelectedSubtypeId(saved.subtypeId || null);
        setSelectedSubtype2Id(saved.subtype2Id || null);
      }
    } catch {}
  }, [persistKey]);

  useEffect(() => {
    localStorage.setItem(
      persistKey,
      JSON.stringify({
        typeId: selectedTypeId,
        subtypeId: selectedSubtypeId,
        subtype2Id: selectedSubtype2Id,
        v: 1,
      })
    );
  }, [persistKey, selectedTypeId, selectedSubtypeId, selectedSubtype2Id]);

  // Loaders (reads)
  const loadTypes = async () => {
    setLoading((s) => ({ ...s, types: true })); setErr(null);
    try {
      const q = new URLSearchParams();
      q.set("sort", "name:asc");
      if (search.trim()) q.set("search", search.trim());
      const res = await axiosInstance.get(`job-types?${q.toString()}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setTypes(data);

      if (!selectedTypeId) {
        const code = (initialShortCode || "").toUpperCase();
        const byShort = data.find((t) => (t.short_code || "").toUpperCase() === code);
        setSelectedTypeId(byShort?.id || data[0]?.id || null);
      }
    } catch (e) {
      console.error("❌ job-types list failed:", e?.response?.data || e?.message || e);
      setErr("Failed to load job types.");
    } finally {
      setLoading((s) => ({ ...s, types: false }));
    }
  };

  const normalizeSubtype = (n) => ({
    id: n.id,
    name: n.name,
    description: n.description ?? "",
    sort_order: n.sort_order ?? 0,
    is_active: typeof n.is_active === "boolean" ? n.is_active : true,
  });
  const normalizeSubtype2 = (n, parentId) => ({
    id: n.id,
    name: n.name,
    description: n.description ?? "",
    sort_order: n.sort_order ?? 0,
    is_active: typeof n.is_active === "boolean" ? n.is_active : true,
    job_subtype_id: n.job_subtype_id ?? parentId ?? null,
  });

  const readTaxonomyForType = async (type) => {
    if (!type) return null;
    try {
      const res = await axiosInstance.get(
        `taxonomy/by-type/${encodeURIComponent((type.short_code || "").toUpperCase())}`
      );
      return res?.data || null;
    } catch (_) {
      try {
        const res = await axiosInstance.get(`taxonomy/tree`);
        const tree = res?.data;
        const items = Array.isArray(tree) ? tree : Array.isArray(tree?.items) ? tree.items : [];
        return items.find((t) => t.id === type.id) || null;
      } catch (e) {
        console.error("❌ taxonomy read failed:", e?.response?.data || e?.message || e);
        return null;
      }
    }
  };

  const loadSubtypes = async (typeId) => {
    setSubtypes2([]); setSelectedSubtype2Id(null);
    if (!typeId) { setSubtypes([]); setSelectedSubtypeId(null); return; }
    setLoading((s) => ({ ...s, subs: true })); setErr(null);
    try {
      const node = await readTaxonomyForType(
        types.find((t) => t.id === typeId) || selectedType
      );
      taxNodeRef.current = node;
      const children =
        (node && Array.isArray(node.subtypes) && node.subtypes) ||
        (node && Array.isArray(node.children) && node.children) || [];
      const list = children.map(normalizeSubtype);
      setSubtypes(list);
      if (!list.some((d) => d.id === selectedSubtypeId)) {
        setSelectedSubtypeId(list[0]?.id || null);
      }
    } finally {
      setLoading((s) => ({ ...s, subs: false }));
    }
  };

  const loadSubtypes2 = async (subtypeId) => {
    if (!subtypeId) { setSubtypes2([]); setSelectedSubtype2Id(null); return; }
    setLoading((s) => ({ ...s, subs2: true })); setErr(null);
    try {
      const node = taxNodeRef.current;
      const subs =
        (node && Array.isArray(node.subtypes) && node.subtypes) ||
        (node && Array.isArray(node.children) && node.children) || [];
      const found = subs.find((s) => s.id === subtypeId);
      const kids =
        (found && Array.isArray(found.subtypes2) && found.subtypes2) ||
        (found && Array.isArray(found.children) && found.children) || [];
      const list = kids.map((n) => normalizeSubtype2(n, subtypeId));
      setSubtypes2(list);
      if (!list.some((d) => d.id === selectedSubtype2Id)) {
        setSelectedSubtype2Id(list[0]?.id || null);
      }
    } finally {
      setLoading((s) => ({ ...s, subs2: false }));
    }
  };

  // Initial + refresh loop
  useEffect(() => { loadTypes(); /* eslint-disable-next-line */ }, [refreshKey]);
  useEffect(() => { loadSubtypes(selectedTypeId); /* eslint-disable-next-line */ }, [selectedTypeId, types]);
  useEffect(() => { loadSubtypes2(selectedSubtypeId); /* eslint-disable-next-line */ }, [selectedSubtypeId]);

  // ---------- Mutations — Types ----------
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeEditing, setTypeEditing] = useState(null);

  const createType = () => { setTypeEditing(null); setTypeModalOpen(true); };
  const editType = () => { if (!selectedType) return; setTypeEditing(selectedType); setTypeModalOpen(true); };

  const saveType = async (form) => {
    // Guard against the common 500s (duplicate code / blank name)
    const short = (form.short_code || "").trim().toUpperCase();
    const name = (form.name || "").trim();
    if (!short || !name) {
      alert("Short Code and Name are required.");
      return;
    }
    if (!typeEditing?.id && types.some((t) => (t.short_code || "").toUpperCase() === short)) {
      alert(`Job Type short code '${short}' already exists.`);
      return;
    }

    const payload = { ...form, short_code: short, name };
    try {
      if (typeEditing?.id) {
        await axiosInstance.put(`job-types/${typeEditing.id}`, payload);
      } else {
        const res = await axiosInstance.post(`job-types`, payload);
        setSelectedTypeId(res.data?.id || selectedTypeId);
      }
      setTypeModalOpen(false);
      await loadTypes();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      alert(`Type save failed (${status || "?"}): ${detail}`);
    }
  };

  const toggleType = async () => {
    if (!selectedType) return;
    try {
      await axiosInstance.patch(
        `job-types/${selectedType.id}/${selectedType.is_active ? "deactivate" : "activate"}`
      );
      await loadTypes();
    } catch (e) {
      alert(`Type toggle failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteType = async () => {
    if (!selectedType) return;
    if (!window.confirm("Delete Job Type and all its subtypes?")) return;
    try {
      await axiosInstance.delete(`job-types/${selectedType.id}`);
      await loadTypes();
      setSubtypes([]); setSubtypes2([]);
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // ---------- Mutations — Subtype ----------
  const [subtypeModalOpen, setSubtypeModalOpen] = useState(false);
  const [subtypeEditing, setSubtypeEditing] = useState(null);

  const createSubtype = () => { setSubtypeEditing(null); setSubtypeModalOpen(true); };
  const editSubtype = () => { if (!selectedSubtype) return; setSubtypeEditing(selectedSubtype); setSubtypeModalOpen(true); };

  const saveSubtype = async (form) => {
    if (!selectedTypeId) return;
    const body = { ...form, job_type_id: selectedTypeId };
    try {
      if (subtypeEditing?.id) {
        await axiosInstance.put(`job-subtypes/${subtypeEditing.id}`, body);
      } else {
        await axiosInstance.post(`job-subtypes`, body);
      }
      setSubtypeModalOpen(false);
      taxNodeRef.current = null;
      await loadSubtypes(selectedTypeId);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Not Found";
      alert(`Subtype save failed: ${detail}${status ? ` (${status})` : ""}`);
    }
  };

  const toggleSubtype = async () => {
    if (!selectedSubtype) return;
    try {
      await axiosInstance.patch(
        `job-subtypes/${selectedSubtype.id}/${selectedSubtype.is_active ? "deactivate" : "activate"}`
      );
      taxNodeRef.current = null;
      await loadSubtypes(selectedTypeId);
    } catch (e) {
      alert(`Subtype toggle failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteSubtype = async () => {
    if (!selectedSubtype) return;
    if (!window.confirm("Delete Subtype and all its Subtype2?")) return;
    try {
      await axiosInstance.delete(`job-subtypes/${selectedSubtype.id}`);
      taxNodeRef.current = null;
      await loadSubtypes(selectedTypeId);
      setSubtypes2([]);
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // ---------- Mutations — Subtype2 ----------
  const [subtype2ModalOpen, setSubtype2ModalOpen] = useState(false);

  const createSubtype2 = () => setSubtype2ModalOpen(true);

  const saveSubtype2 = async (form) => {
    if (!selectedSubtypeId) return;
    const body = { ...form, job_subtype_id: selectedSubtypeId };
    try {
      await axiosInstance.post(`job-subtypes2`, body);
      setSubtype2ModalOpen(false);
      taxNodeRef.current = null;
      await loadSubtypes2(selectedSubtypeId);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Not Found";
      alert(`Subtype2 create failed: ${detail}${status ? ` (${status})` : ""}`);
    }
  };

  const updateSubtype2 = async (item, patch) => {
    try {
      await axiosInstance.put(`job-subtypes2/${item.id}`, {
        ...item,
        ...patch,
        job_subtype_id: item.job_subtype_id,
      });
      taxNodeRef.current = null;
      await loadSubtypes2(item.job_subtype_id);
    } catch (e) {
      alert(`Subtype2 update failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteSubtype2 = async (id) => {
    if (!window.confirm("Delete Subtype2?")) return;
    try {
      await axiosInstance.delete(`job-subtypes2/${id}`);
      taxNodeRef.current = null;
      await loadSubtypes2(selectedSubtypeId);
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Emit to job form
  const emitSelection = () => {
    onSelect?.({
      job_type: selectedType || null,
      job_subtype: selectedSubtype || null,
      job_subtype2: selectedSubtype2 || null,
    });
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const body = bodyRef.current;

    if (!root || !body) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const panePairs = [
      [typesViewportRef, firstTypeRowRef],
      [subtypesViewportRef, firstSubtypeRowRef],
      [subtypes2ViewportRef, firstSubtype2RowRef],
    ];

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const clearPaneSizing = () => {
      panePairs.forEach(([viewportRef]) => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        viewport.style.height = "";
        viewport.style.minHeight = "";
      });
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();
      clearPaneSizing();

      root.style.height = "";
      root.style.minHeight = "";
      body.style.height = "";
      body.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        panePairs.forEach(([viewportRef, firstRowRef]) => {
          const viewport = viewportRef.current;
          const firstRow = firstRowRef.current;

          if (!viewport || !firstRow) {
            return;
          }

          const rowHeight = Math.ceil(
            Math.max(
              firstRow.getBoundingClientRect().height || 0,
              firstRow.scrollHeight || 0
            )
          );

          if (Number.isFinite(rowHeight) && rowHeight > 0) {
            viewport.style.height = `${rowHeight}px`;
            viewport.style.minHeight = `${rowHeight}px`;
          }
        });

        root.style.height = "auto";
        root.style.minHeight = "0px";
        body.style.height = "auto";
        body.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    panePairs.forEach(([, firstRowRef]) => {
      if (resizeObserver && firstRowRef.current) {
        resizeObserver.observe(firstRowRef.current);
      }
    });

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();
      clearPaneSizing();

      root.style.height = "";
      root.style.minHeight = "";
      body.style.height = "";
      body.style.minHeight = "";
    };
  }, [
    err,
    loading.subs,
    loading.subs2,
    loading.types,
    search,
    selectedSubtypeId,
    selectedTypeId,
    subtypes.length,
    subtypes2.length,
    types.length,
  ]);

  // UI
  return (
    <div
      ref={widgetRootRef}
      className={
        "h-auto min-h-0 w-full overflow-visible rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full md:overflow-hidden " +
        (className || "")
      }
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-taxonomy-pane-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-taxonomy-pane-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-taxonomy-row {
            min-width: 0;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-taxonomy-row button {
            min-height: 36px;
            padding-left: 6px;
            padding-right: 6px;
          }
        }
      `}</style>
      {/* Header */}
      <div className="bg-accent text-white px-3 py-3 flex flex-col gap-3 md:px-4 md:py-2 md:flex-row md:flex-wrap md:justify-between md:items-center">
        <span className="text-sm font-semibold">
          {getWidgetTitle("taxonomy_admin") || "Taxonomy Admin"}
        </span>

        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:flex md:w-auto md:items-center">
          <TextInput
            placeholder="Search job types..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-[180px]"
          />
          <button
            onClick={() => loadTypes()}
            className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
          >
            Search
          </button>

          <button
            onClick={emitSelection}
            className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs font-semibold px-3 py-1 rounded shadow md:min-h-0 md:w-auto"
            title="Use current selection in Job form"
          >
            Use Selection
          </button>
        </div>
      </div>

      {/* Body: three-column slim lists */}
      <div ref={bodyRef} className="grid h-auto min-h-0 grid-cols-1 gap-3 p-3 md:h-full md:grid-cols-3">
        {/* TYPES */}
        <div className="flex h-auto min-h-0 flex-col rounded border md:h-full">
          <div className="flex min-w-0 flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-semibold text-sm">Job Types</div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={createType}
                className="min-h-[36px] bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-2 py-1 rounded shadow"
              >
                + New
              </button>
              <button
                onClick={editType}
                disabled={!selectedType}
                className="min-h-[36px] bg-gray-200 text-gray-800 disabled:opacity-50 text-xs font-semibold px-2 py-1 rounded shadow"
              >
                Edit
              </button>
            </div>
          </div>
          <div ref={typesViewportRef} className="vx-taxonomy-pane-viewport h-auto flex-none overflow-visible text-sm md:h-auto md:flex-1 md:overflow-auto">
            {loading.types ? (
              <div className="p-3 text-gray-500 italic">Loading…</div>
            ) : types.length === 0 ? (
              <div className="p-3 text-gray-500 italic">No job types.</div>
            ) : (
              <ul>
                {types.map((t, index) => (
                  <li
                    key={t.id}
                    ref={index === 0 ? firstTypeRowRef : null}
                    onClick={() => setSelectedTypeId(t.id)}
                    className={
                      "vx-taxonomy-row min-w-0 px-3 py-2 border-b cursor-pointer " +
                      (selectedTypeId === t.id ? "bg-blue-50" : "bg-white")
                    }
                    title={t.description || t.name}
                  >
                    <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="min-w-0 break-words font-medium">{t.name}</span>
                      <span className="text-xs text-gray-500">({t.short_code})</span>
                    </div>
                    <div className="mt-1 flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="min-w-0 break-words text-[11px] text-gray-600 md:line-clamp-1">
                        {t.description || "\u00A0"}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleType();
                          }}
                          className="min-h-[36px] text-[11px] underline"
                          title={t.is_active ? "Deactivate" : "Activate"}
                        >
                          {t.is_active ? "Active" : "Inactive"}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteType();
                          }}
                          className="min-h-[36px] text-[11px] text-red-700 underline"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* SUBTYPES */}
        <div className="flex h-auto min-h-0 flex-col rounded border md:h-full">
          <div className="flex min-w-0 flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-semibold text-sm">
              Subtypes{" "}
              <span className="text-gray-500">
                {selectedType ? `(${selectedType.name})` : ""}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={createSubtype}
                disabled={!selectedType}
                className="min-h-[36px] bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-2 py-1 rounded shadow disabled:opacity-50"
              >
                + New
              </button>
              <button
                onClick={editSubtype}
                disabled={!selectedSubtype}
                className="min-h-[36px] bg-gray-200 text-gray-800 disabled:opacity-50 text-xs font-semibold px-2 py-1 rounded shadow"
              >
                Edit
              </button>
            </div>
          </div>
          <div ref={subtypesViewportRef} className="vx-taxonomy-pane-viewport h-auto flex-none overflow-visible text-sm md:h-auto md:flex-1 md:overflow-auto">
            {loading.subs ? (
              <div className="p-3 text-gray-500 italic">Loading…</div>
            ) : subtypes.length === 0 ? (
              <div className="p-3 text-gray-500 italic">No subtypes.</div>
            ) : (
              <ul>
                {subtypes.map((s, index) => (
                  <li
                    key={s.id}
                    ref={index === 0 ? firstSubtypeRowRef : null}
                    onClick={() => setSelectedSubtypeId(s.id)}
                    className={
                      "vx-taxonomy-row min-w-0 px-3 py-2 border-b cursor-pointer " +
                      (selectedSubtypeId === s.id ? "bg-blue-50" : "bg-white")
                    }
                    title={s.description || s.name}
                  >
                    <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="min-w-0 break-words font-medium">{s.name}</span>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubtype();
                          }}
                          className="min-h-[36px] text-[11px] underline"
                          title={s.is_active ? "Deactivate" : "Activate"}
                        >
                          {s.is_active ? "Active" : "Inactive"}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSubtype();
                          }}
                          className="min-h-[36px] text-[11px] text-red-700 underline"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 min-w-0 break-words text-[11px] text-gray-600 md:line-clamp-1">
                      {s.description || "\u00A0"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* SUBTYPES2 */}
        <div className="flex h-auto min-h-0 flex-col rounded border md:h-full">
          <div className="flex min-w-0 flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-semibold text-sm">
              Subtype 2{" "}
              <span className="text-gray-500">{selectedSubtype?.name || ""}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={createSubtype2}
                disabled={!selectedSubtype}
                className="min-h-[36px] bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-2 py-1 rounded shadow disabled:opacity-50"
              >
                + New
              </button>
            </div>
          </div>
          <div ref={subtypes2ViewportRef} className="vx-taxonomy-pane-viewport h-auto flex-none overflow-visible text-sm md:h-auto md:flex-1 md:overflow-auto">
            {loading.subs2 ? (
              <div className="p-3 text-gray-500 italic">Loading…</div>
            ) : subtypes2.length === 0 ? (
              <div className="p-3 text-gray-500 italic">No subtype2.</div>
            ) : (
              <ul>
                {subtypes2.map((s2, index) => (
                  <li
                    key={s2.id}
                    ref={index === 0 ? firstSubtype2RowRef : null}
                    className="vx-taxonomy-row flex min-w-0 flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    title={s2.description || s2.name}
                  >
                    <div className="min-w-0">
                      <div className="min-w-0 break-words font-medium">{s2.name}</div>
                      <div className="min-w-0 break-words text-[11px] text-gray-600 md:line-clamp-1">
                        {s2.description || "\u00A0"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => updateSubtype2(s2, { is_active: !s2.is_active })}
                        className="min-h-[36px] text-[11px] underline"
                        title={s2.is_active ? "Deactivate" : "Activate"}
                      >
                        {s2.is_active ? "Active" : "Inactive"}
                      </button>
                      <button
                        onClick={() => deleteSubtype2(s2.id)}
                        className="min-h-[36px] text-[11px] text-red-700 underline"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Footer errors */}
      {err && (
        <div className="break-words px-3 py-2 text-sm text-red-700 bg-red-50 border-t border-red-100">
          {err}
        </div>
      )}

      {/* Modals */}
      <TypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        initial={typeEditing}
        onSave={saveType}
      />
      <SubtypeModal
        open={subtypeModalOpen}
        onClose={() => setSubtypeModalOpen(false)}
        initial={subtypeEditing}
        onSave={saveSubtype}
        parentType={selectedType}
      />
      <Subtype2Modal
        open={subtype2ModalOpen}
        onClose={() => setSubtype2ModalOpen(false)}
        onSave={saveSubtype2}
        parentSubtype={selectedSubtype}
      />
    </div>
  );
};

export default TaxonomyAdminWidget;
